/**
 * Shared scorecard UI primitives — reused across My Scorecard, Channel Dashboard,
 * IB Management Dashboard, and UA Dashboard.
 */
import React from 'react';
import { TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2, HelpCircle } from 'lucide-react';

export const STATUS_META = {
  on_track:  { label: 'On Track',  color: '#10B981', bg: 'bg-[#10B981]/10',  border: 'border-[#10B981]/30', text: 'text-[#10B981]',  icon: CheckCircle2 },
  at_risk:   { label: 'At Risk',   color: '#F59E0B', bg: 'bg-[#F59E0B]/10',  border: 'border-[#F59E0B]/30', text: 'text-[#F59E0B]',  icon: AlertTriangle },
  off_track: { label: 'Off Track', color: '#E63946', bg: 'bg-[#E63946]/10',  border: 'border-[#E63946]/30', text: 'text-[#E63946]',  icon: TrendingDown },
  no_data:   { label: 'No Data',   color: '#6B7280', bg: 'bg-[#6B7280]/10',  border: 'border-[#6B7280]/30', text: 'text-[#6B7280]',  icon: HelpCircle },
};

export const CATEGORY_META = {
  REVENUE:    { label: 'Revenue',    color: '#10B981' },
  OPERATION:  { label: 'Operation',  color: '#3B82F6' },
  OPEARATION: { label: 'Operation',  color: '#3B82F6' },
  SALES:      { label: 'Sales',      color: '#F59E0B' },
  MARKETING:  { label: 'Marketing',  color: '#8B5CF6' },
  CREATIVE:   { label: 'Creative',   color: '#EC4899' },
  CUSTOMER:   { label: 'Customer',   color: '#06B6D4' },
  EDUCATION:  { label: 'Education',  color: '#14B8A6' },
  PRODUCT:    { label: 'Product',    color: '#6366F1' },
  SIGNAL:     { label: 'Signal',     color: '#F43F5E' },
  GROWTH:     { label: 'Growth',     color: '#A855F7' },
  OTHER:      { label: 'Other',      color: '#6B7280' },
};

// Code-level category override (frontend re-categorization).
// CPL is conceptually a Sales efficiency metric, not Revenue.
export const CATEGORY_OVERRIDES = {
  CPL: 'SALES',
};

export const PERSPECTIVE_META = {
  FINANCIAL:        { label: 'Financial',        color: '#10B981' },
  CUSTOMER:         { label: 'Customer',         color: '#3B82F6' },
  INTERNAL_PROCESS: { label: 'Internal Process', color: '#F59E0B' },
  INTERNAL:         { label: 'Internal',         color: '#F59E0B' },
  LEARNING:         { label: 'Learning',         color: '#8B5CF6' },
  GROWTH:           { label: 'Growth',           color: '#8B5CF6' },
};

const formatValue = (v, unit) => {
  const n = Number(v) || 0;
  if (unit === 'RM' || unit === 'MYR') return `RM ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (unit === '$' || unit === 'USD') return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (unit === '%') return `${n.toFixed(1)}%`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export function StatusBadge({ status, size = 'sm' }) {
  const meta = STATUS_META[status] || STATUS_META.no_data;
  const Icon = meta.icon;
  const dims = size === 'lg' ? 'text-sm px-3 py-1.5' : 'text-xs px-2 py-0.5';
  return (
    <span className={`inline-flex items-center gap-1.5 ${meta.bg} ${meta.border} border ${meta.text} ${dims} rounded-full font-semibold`} data-testid={`status-badge-${status}`}>
      <Icon size={size === 'lg' ? 14 : 11} />
      {meta.label}
    </span>
  );
}

export function ProgressBar({ pct, status }) {
  const safe = Math.min(Math.max(pct || 0, 0), 100);
  const meta = STATUS_META[status] || STATUS_META.no_data;
  return (
    <div className="w-full bg-[hsl(var(--surface-elevated))] rounded-full h-1.5 overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${safe}%`, backgroundColor: meta.color }} />
    </div>
  );
}

export function Sparkline({ data, width = 100, height = 26, color, status }) {
  // data = [{period, overall_pct, status}] OR array of numbers
  if (!Array.isArray(data) || data.length === 0) return null;
  const values = data.map(d => typeof d === 'number' ? d : Number(d.overall_pct) || 0);
  const max = Math.max(100, ...values);
  const min = 0;
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return [x, y];
  });
  const pathD = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  // Area fill (close to bottom)
  const areaD = pathD + ` L ${points[points.length - 1][0].toFixed(1)} ${height} L ${points[0][0].toFixed(1)} ${height} Z`;
  // Color from last status if not provided
  const lastStatus = status || (typeof data[data.length - 1] === 'object' ? data[data.length - 1].status : null) || 'no_data';
  const c = color || (STATUS_META[lastStatus]?.color) || '#3B82F6';
  const lastVal = values[values.length - 1];
  const lastY = points[points.length - 1][1];
  const gid = `sparkgrad-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg width={width} height={height} className="overflow-visible" data-testid="sparkline">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={c} stopOpacity="0.35" />
          <stop offset="100%" stopColor={c} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gid})`} />
      <path d={pathD} fill="none" stroke={c} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={points[points.length - 1][0]} cy={lastY} r="2.5" fill={c} />
      <title>{values.map((v, i) => `${typeof data[i] === 'object' ? data[i].period : i}: ${v.toFixed(1)}%`).join('\n')}</title>
    </svg>
  );
}

export function MiniHBar({ value, max = 100, color, width = 60, height = 6 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className="rounded-full overflow-hidden bg-[hsl(var(--surface-elevated))]" style={{ width, height }}>
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color || '#10B981' }} />
    </div>
  );
}


export function StatusDonut({ overall, size = 84 }) {
  // SVG donut chart of on_track / at_risk / off_track distribution
  const total = (overall?.templates_count || 0);
  if (total === 0) return null;
  const on = overall?.on_track_count || 0;
  const at = overall?.at_risk_count || 0;
  const off = overall?.off_track_count || 0;
  const radius = (size - 12) / 2;
  const circ = 2 * Math.PI * radius;
  const seg = (v) => (v / total) * circ;
  let offset = 0;
  const segments = [];
  [['on', on, STATUS_META.on_track.color],
   ['at', at, STATUS_META.at_risk.color],
   ['off', off, STATUS_META.off_track.color]].forEach(([k, v, color]) => {
    if (v > 0) {
      segments.push({ key: k, color, len: seg(v), offset });
      offset += seg(v);
    }
  });
  return (
    <svg width={size} height={size} className="-rotate-90" data-testid="status-donut">
      <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--surface-elevated))" strokeWidth="6" />
      {segments.map(s => (
        <circle key={s.key} cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={s.color} strokeWidth="6"
          strokeDasharray={`${s.len} ${circ - s.len}`}
          strokeDashoffset={-s.offset}
          style={{ transition: 'stroke-dasharray 0.6s ease-out' }}
        />
      ))}
    </svg>
  );
}

function AnimatedNumber({ value, duration = 800, suffix = '' }) {
  const [display, setDisplay] = React.useState(0);
  React.useEffect(() => {
    let start = 0;
    const t0 = performance.now();
    const target = Number(value) || 0;
    let raf;
    const tick = (t) => {
      const p = Math.min((t - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(start + (target - start) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [value, duration]);
  return <>{display.toFixed(1)}{suffix}</>;
}

export function OverallSummaryCard({ overall, label = 'Overall Score' }) {
  const meta = STATUS_META[overall?.status || 'no_data'];
  return (
    <div className={`relative overflow-hidden ${meta.bg} border ${meta.border} rounded-xl p-5`} data-testid="overall-summary-card">
      {/* Subtle radial glow */}
      <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ backgroundColor: meta.color }} />
      <div className="relative flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="text-[#9CA3AF] text-xs uppercase tracking-wider mb-1">{label}</p>
          <div className="flex items-baseline gap-2">
            <span className={`text-5xl font-bold ${meta.text} tabular-nums`}>
              <AnimatedNumber value={Math.min(overall?.overall_pct || 0, 100)} suffix="" />
              <span className="text-xl">%</span>
            </span>
            <StatusBadge status={overall?.status || 'no_data'} />
          </div>
          <div className="mt-3">
            <ProgressBar pct={overall?.overall_pct || 0} status={overall?.status || 'no_data'} />
          </div>
          <div className="flex gap-4 mt-3 text-[11px]">
            <span className="flex items-center gap-1.5 text-[#10B981]"><span className="w-2 h-2 rounded-full bg-[#10B981]" />On Track {overall?.on_track_count || 0}</span>
            <span className="flex items-center gap-1.5 text-[#F59E0B]"><span className="w-2 h-2 rounded-full bg-[#F59E0B]" />At Risk {overall?.at_risk_count || 0}</span>
            <span className="flex items-center gap-1.5 text-[#E63946]"><span className="w-2 h-2 rounded-full bg-[#E63946]" />Off Track {overall?.off_track_count || 0}</span>
          </div>
        </div>
        <StatusDonut overall={overall} size={84} />
      </div>
    </div>
  );
}

export function MetricRow({ template, animationDelay = 0 }) {
  const meta = STATUS_META[template.status] || STATUS_META.no_data;
  const cat = (template.category || '').toUpperCase();
  const cMeta = CATEGORY_META[cat] || { color: '#6B7280', label: cat || '—' };
  return (
    <div
      className="grid grid-cols-12 gap-3 items-center bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-4 py-3 hover:border-[hsl(var(--border-hover))] hover:-translate-y-px transition-all duration-200 sc-fade-in"
      style={{ animationDelay: `${animationDelay}ms` }}
      data-testid={`metric-row-${template.template_id}`}
    >
      <div className="col-span-4">
        <div className="flex items-center gap-1.5 mb-0.5">
          <p className="text-white text-sm font-semibold">{template.name}</p>
          {template.crm_connected ? (
            <span className="inline-flex items-center gap-1 bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981] text-[9px] px-1.5 py-0.5 rounded-full font-medium" title="Live data from CRM">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />CRM Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 bg-[#6B7280]/10 border border-[#6B7280]/30 text-[#6B7280] text-[9px] px-1.5 py-0.5 rounded-full" title="Not connected to CRM — using external Scorecard data only">
              <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280]" />Not connected
            </span>
          )}
        </div>
        <p className="text-[10px] text-[#6B7280]">{template.code} · <span style={{ color: cMeta.color }}>{cMeta.label}</span></p>
      </div>
      <div className="col-span-2 text-right">
        <p className="text-[10px] text-[#9CA3AF] uppercase">Target</p>
        <p className="text-white text-sm font-mono">{formatValue(template.target, template.unit)}</p>
      </div>
      <div className="col-span-2 text-right">
        <p className="text-[10px] text-[#9CA3AF] uppercase">Achievement</p>
        <p className={`text-sm font-mono font-semibold ${meta.text}`}>{formatValue(template.achievement, template.unit)}</p>
      </div>
      <div className="col-span-2">
        <div className="flex items-center justify-end gap-2 mb-1">
          <span className={`text-sm font-bold ${meta.text}`}>{Math.min(template.achievement_pct, 100).toFixed(1)}%</span>
        </div>
        <ProgressBar pct={template.achievement_pct} status={template.status} />
      </div>
      <div className="col-span-2 flex justify-end">
        <StatusBadge status={template.status} />
      </div>
    </div>
  );
}

export function CategoryGroup({ categoryKey, templates }) {
  const meta = CATEGORY_META[categoryKey] || { label: categoryKey || 'Other', color: '#6B7280' };
  const onTrack = templates.filter(t => t.status === 'on_track').length;
  const connected = templates.filter(t => t.crm_connected).length;
  return (
    <div className="space-y-2" data-testid={`category-group-${categoryKey}`}>
      <div className="flex items-center justify-between px-1 mb-1">
        <div className="flex items-center gap-2.5">
          <div className="w-1 h-5 rounded-sm" style={{ backgroundColor: meta.color }} />
          <h3 className="text-white text-sm font-semibold tracking-tight" style={{ color: meta.color }}>{meta.label}</h3>
          <span className="text-[10px] text-[#6B7280]">{templates.length} metric{templates.length === 1 ? '' : 's'}</span>
        </div>
        <div className="flex items-center gap-3 text-[10px]">
          <span className="text-[#10B981]">{onTrack}/{templates.length} on track</span>
          <span className="text-[#9CA3AF]">{connected} live</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {templates.map((t, i) => <MetricRow key={t.template_id} template={t} animationDelay={i * 40} />)}
      </div>
    </div>
  );
}

export function ScorecardBreakdown({ breakdown }) {
  if (!breakdown || breakdown.length === 0) {
    return (
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-8 text-center" data-testid="scorecard-empty">
        <HelpCircle size={28} className="text-[#6B7280] mx-auto mb-2" />
        <p className="text-[#9CA3AF] text-sm">Tiada metric data untuk period ni.</p>
        <p className="text-[#6B7280] text-xs mt-1">Pastikan admin dah sync scorecard snapshot terkini.</p>
      </div>
    );
  }
  // Group by category (preferred for the new Operation/Marketing/Creative/Sales split)
  const groups = breakdown.reduce((acc, t) => {
    let c = (t.category || 'OTHER').toUpperCase();
    if (c === 'OPEARATION') c = 'OPERATION';
    // Apply UI-side category overrides (e.g. CPL → SALES)
    const code = (t.code || '').toUpperCase();
    if (CATEGORY_OVERRIDES[code]) c = CATEGORY_OVERRIDES[code];
    if (!acc[c]) acc[c] = [];
    acc[c].push(t);
    return acc;
  }, {});
  // Preferred order
  const order = ['REVENUE', 'SALES', 'MARKETING', 'CREATIVE', 'OPERATION', 'PRODUCT', 'SIGNAL', 'EDUCATION', 'CUSTOMER', 'GROWTH'];
  const ordered = [...order.filter(c => groups[c]), ...Object.keys(groups).filter(c => !order.includes(c))];
  return (
    <div className="space-y-5">
      {ordered.map(c => <CategoryGroup key={c} categoryKey={c} templates={groups[c]} />)}
    </div>
  );
}

export function PeriodSelector({ value, onChange, available = [] }) {
  const now = new Date();
  const optionsMap = new Map();
  // Build last 12 months
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
    optionsMap.set(v, { value: v, label: i === 0 ? `${label} (MTD)` : label, hasData: available.includes(v) });
  }
  // Also include any available period that's older than 12 months
  available.forEach(v => {
    if (!optionsMap.has(v)) {
      const [y, m] = v.split('-');
      const d = new Date(parseInt(y), parseInt(m) - 1, 1);
      const label = d.toLocaleDateString('en-MY', { month: 'short', year: 'numeric' });
      optionsMap.set(v, { value: v, label, hasData: true });
    }
  });
  const options = Array.from(optionsMap.values()).sort((a, b) => b.value.localeCompare(a.value));

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid="period-selector"
      className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#10B981]/50"
    >
      {options.map(o => (
        <option key={o.value} value={o.value}>
          {o.label}{available.length > 0 && !o.hasData ? ' (no data)' : ''}
        </option>
      ))}
    </select>
  );
}
